"use client";

import React, { useRef } from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  useToast,
  Spinner,
  Badge,
  Select,
  Grid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  Tabs,
  TabList,
  Tab,
  VStack,
  IconButton,
  Avatar,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Link,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faPhone, faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  agentService,
  type Agent,
  type VoiceDetails,
  type LLMData,
} from "../voice-services/agentService";
import { phoneNumberService } from "../voice-services/phoneNumberService";
import { getDisplayPhonePretty } from "../voice-services/voicePhoneDisplay";
import {
  listInboundAgentIds,
  listOutboundAgentIds,
} from "../voice-services/phoneNumberAgents";
import GenderImage from "./voice-agents/voices/GenderImages";
import { CreateAgent } from "./voice-agents/CreateAgent";
import { ImportAgent } from "./voice-agents/ImportAgent";
import VoicePhoneNumbers from "./voice-agents/VoicePhoneNumbers";
import VoiceKnowledgeBase from "./voice-agents/VoiceKnowledgeBase";
import { useAuth } from "../contexts/AuthContext";
import { useStableCredits } from "../hooks/useStableCredits";
import { supabase } from "../supabaseClient";

const MotionBox = motion(Box);

async function fetchInBatches<T, R>(
  items: T[],
  batchSize: number,
  handler: (item: T) => Promise<R>,
  signal: AbortSignal,
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(handler));
    results.push(...batchResults);
  }
  return results;
}

const VoiceAgents = () => {
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [phoneNumbers, setPhoneNumbers] = React.useState<
    Record<string, string[]>
  >({});
  const [voiceDetails, setVoiceDetails] = React.useState<
    Record<string, VoiceDetails>
  >({});
  const [llmData, setLlmData] = React.useState<Record<string, LLMData>>({});

  const [stats, setStats] = React.useState({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
    avgSuccessRate: 0,
    avgDuration: "0:00",
  });

  // CHANGE: granular loading (dynamic loading like All Agents page)
  const [loading, setLoading] = React.useState({
    agents: true,
    phoneNumbers: true,
    metrics: true,
    voiceDetails: true,
    llmData: true,
  });

  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "Voice";
    },
  );

  const [selectedVoiceTab, setSelectedVoiceTab] = React.useState<string>(() => {
    return localStorage.getItem("currentAgentTab") || "agents";
  });

  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, currentClientId, clients, userProfile } = useAuth();
  const credits = useStableCredits();

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [deleteAgentId, setDeleteAgentId] = React.useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [pausingAgentId, setPausingAgentId] = React.useState<string | null>(null);

  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient
      ? currentClient.company_name
      : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  const fetchData = React.useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // CHANGE: do NOT blank UI on refresh; show tiny spinners while revalidating
    setLoading((prev) => ({
      ...prev,
      agents: true,
      phoneNumbers: true,
      metrics: true,
      // details load after agents, but keep previous values and show spinner in-place
      voiceDetails: false,
      llmData: false,
    }));

    const defaultStats = {
      totalAgents: 0,
      activeAgents: 0,
      totalInteractions: 0,
      avgSuccessRate: 0,
      avgDuration: "0:00",
    };

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        console.error("No active session");
        if (!signal.aborted) {
          setLoading((prev) => ({
            ...prev,
            agents: false,
            phoneNumbers: false,
            metrics: false,
          }));
        }
        return;
      }

      // CHANGE: fetch core pieces in parallel, but update UI as each resolves
      void agentService
        .list(companyName)
        .then((freshAgents) => {
          if (signal.aborted) return;

          const agentMap = new Map<string, Agent>();
          freshAgents.forEach((agent: Agent) =>
            agentMap.set(agent.agent_id, agent),
          );
          const uniqueAgents = Array.from(agentMap.values());
          setAgents(uniqueAgents);

          // Now that we have agents, begin background loading voice+llm details
          void (async () => {
            try {
              setLoading((p) => ({ ...p, voiceDetails: true, llmData: true }));

              const uniqueVoiceIds = Array.from(
                new Set(uniqueAgents.map((a) => a.voice_id).filter(Boolean)),
              );

              if (uniqueVoiceIds.length > 0) {
                // Progressive updates per batch
                const batchSize = 6;
                for (let i = 0; i < uniqueVoiceIds.length; i += batchSize) {
                  if (signal.aborted) return;
                  const batch = uniqueVoiceIds.slice(i, i + batchSize);
                  const batchResults = await Promise.all(
                    batch.map((id) => agentService.getVoiceDetails(id)),
                  );
                  if (signal.aborted) return;

                  const partial: Record<string, VoiceDetails> = {};
                  batch.forEach((id, idx) => {
                    partial[id] = batchResults[idx];
                  });
                  setVoiceDetails((prev) => ({ ...prev, ...partial }));
                }
              }

              const uniqueLlmIds = Array.from(
                new Set(
                  uniqueAgents
                    .map((a) =>
                      a.response_engine?.type === "retell-llm"
                        ? a.response_engine.llm_id
                        : null,
                    )
                    .filter(Boolean) as string[],
                ),
              );

              if (uniqueLlmIds.length > 0) {
                const batchSize = 6;
                for (let i = 0; i < uniqueLlmIds.length; i += batchSize) {
                  if (signal.aborted) return;
                  const batch = uniqueLlmIds.slice(i, i + batchSize);
                  const batchResults = await Promise.all(
                    batch.map((id) => agentService.getLLMData(id)),
                  );
                  if (signal.aborted) return;

                  const partial: Record<string, LLMData> = {};
                  batch.forEach((id, idx) => {
                    partial[id] = batchResults[idx];
                  });
                  setLlmData((prev) => ({ ...prev, ...partial }));
                }
              }
            } catch (error: any) {
              if (error?.name === "AbortError") return;
              console.error("Error loading voice/llm details:", error);
            } finally {
              if (!signal.aborted) {
                setLoading((p) => ({
                  ...p,
                  voiceDetails: false,
                  llmData: false,
                }));
              }
            }
          })();
        })
        .catch((error: any) => {
          if (error?.name === "AbortError") return;
          console.error("Error loading agents:", error);
          toast({ title: 'Failed to load agents', description: 'Please refresh the page.', status: 'error', duration: 5000 });
        })
        .finally(() => {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, agents: false }));
          }
        });

      void phoneNumberService
        .list()
        .then((freshPhoneNumbers) => {
          if (signal.aborted) return;

          const agentPhoneMap: Record<string, string[]> = {};
          freshPhoneNumbers.forEach((number: any) => {
            const display = getDisplayPhonePretty(number);
            listInboundAgentIds(number).forEach((id) => {
              (agentPhoneMap[id] ??= []).push(display);
            });
            listOutboundAgentIds(number).forEach((id) => {
              (agentPhoneMap[id] ??= []).push(display);
            });
          });

          setPhoneNumbers(agentPhoneMap);
        })
        .catch((error: any) => {
          if (error?.name === "AbortError") return;
          console.error("Error loading phone numbers:", error);
        })
        .finally(() => {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, phoneNumbers: false }));
          }
        });

      void (async () => {
        const url = new URL(
          `${process.env.REACT_APP_SERVER_URL}/api/voice-agents/stats`,
        );
        if (companyName) url.searchParams.append("company_name", companyName);

        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${session.user.id}` },
          signal,
        });

        if (signal.aborted) return;

        if (response.ok) {
          const freshStats = await response.json();
          if (!signal.aborted) setStats(freshStats);
        } else {
          if (!signal.aborted) setStats(defaultStats);
        }
      })()
        .catch((error: any) => {
          if (error?.name === "AbortError") return;
          console.error("Error loading stats:", error);
        })
        .finally(() => {
          if (!signal.aborted) {
            setLoading((prev) => ({ ...prev, metrics: false }));
          }
        });
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error loading data:", error);
      toast({ title: "Error loading data", status: "error", duration: 3000 });
      if (!signal.aborted) {
        setLoading((prev) => ({
          ...prev,
          agents: false,
          phoneNumbers: false,
          metrics: false,
          voiceDetails: false,
          llmData: false,
        }));
      }
    }
  }, [companyName, toast]);

  React.useEffect(() => {
    const handleTabChange = (event: any) => {
      setSelectedVoiceTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    const isInternalNavigation = sessionStorage.getItem("agentsInternalNav");

    if (!isInternalNavigation) {
      const savedAgentType = localStorage.getItem("currentAgentType");
      const savedTab = localStorage.getItem("currentAgentTab");

      if (savedAgentType && savedAgentType !== selectedAgentType) {
        setSelectedAgentType(savedAgentType);
      }

      if (savedTab && savedTab !== selectedVoiceTab) {
        setSelectedVoiceTab(savedTab);
      }
    }

    sessionStorage.removeItem("agentsInternalNav");

    window.addEventListener("voiceTabChange", handleTabChange);
    window.addEventListener("agentTabChange", handleTabChange);

    return () => {
      window.removeEventListener("voiceTabChange", handleTabChange);
      window.removeEventListener("agentTabChange", handleTabChange);
    };
  }, []);

  React.useEffect(() => {
    const handleAgentTypeChange = () => {
      const newType = localStorage.getItem("currentAgentType") || "Voice";
      if (newType !== selectedAgentType) setSelectedAgentType(newType);
    };

    window.addEventListener("agentTypeChange", handleAgentTypeChange);
    return () =>
      window.removeEventListener("agentTypeChange", handleAgentTypeChange);
  }, [selectedAgentType]);

  React.useEffect(() => {
    if (location.pathname.startsWith("/voice-agents")) {
      setSelectedAgentType("Voice");
      localStorage.setItem("currentAgentType", "Voice");
    }
  }, [location.pathname]);

  React.useEffect(() => {
    fetchData();
    return () => abortControllerRef.current?.abort();
  }, [fetchData]);

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await agentService.delete(agentId);
      setAgents((prev) => prev.filter((a) => a.agent_id !== agentId));
      toast({
        title: "Agent deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchData();
    } catch {
      toast({ title: "Error deleting agent", status: "error", duration: 3000 });
    }
  };

  const handlePauseResumeAgent = async (agentId: string, currentlyActive: boolean) => {
    setPausingAgentId(agentId);
    // Optimistic update
    setAgents((prev) =>
      prev.map((a) =>
        a.agent_id === agentId ? { ...a, is_active: !currentlyActive } : a,
      ),
    );
    try {
      const action = currentlyActive ? "pause" : "resume";
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(`/api/agents/voice/${agentId}/${action}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(session ? { "user-id": session.user.id } : {}),
        },
      });
      if (!res.ok) {
        // Revert on failure
        setAgents((prev) =>
          prev.map((a) =>
            a.agent_id === agentId ? { ...a, is_active: currentlyActive } : a,
          ),
        );
        throw new Error("Request failed");
      }
      toast({
        title: currentlyActive ? "Agent paused" : "Agent resumed",
        status: "success",
        duration: 3000,
      });
    } catch {
      setAgents((prev) =>
        prev.map((a) =>
          a.agent_id === agentId ? { ...a, is_active: currentlyActive } : a,
        ),
      );
      toast({ title: "Failed to update agent status", status: "error", duration: 3000 });
    } finally {
      setPausingAgentId(null);
    }
  };

  const getVoiceAgentType = (agent: any, llmData: Record<string, any>) => {
    const engineType = agent?.response_engine?.type;

    // Custom LLM
    if (engineType === "custom-llm") {
      return { agentTypeLabel: "Custom LLM", agentTypeColor: "cyan" };
    }

    // Retell LLM
    const llm =
      engineType === "retell-llm"
        ? llmData?.[agent?.response_engine?.llm_id]
        : null;

    const hasStates = Array.isArray(llm?.states) && llm.states.length > 0;

    if (hasStates) {
      return { agentTypeLabel: "Multi-Prompt", agentTypeColor: "blue" };
    }

    return { agentTypeLabel: "Single-Prompt", agentTypeColor: "purple" };
  };

  const handleAgentCreated = async (newAgentId?: string) => {
    if (newAgentId) {
      try {
        const newAgent = await agentService.get(newAgentId);
        setAgents((prev) => [newAgent, ...prev]);
      } catch (error) {
        console.error("Error fetching new agent:", error);
      }
    }
    fetchData();
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "All") navigate("/all-agents");
    else if (type === "Voice") navigate("/voice-agents");
    else if (type === "SMS") navigate("/sms-agents");
    else if (type === "Email") navigate("/email-agents");
    else if (type === "WhatsApp") navigate("/whatsapp-agents");
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    setSelectedVoiceTab(tabs[index]);
    localStorage.setItem("currentAgentTab", tabs[index]);
    window.dispatchEvent(
      new CustomEvent("agentTabChange", { detail: tabs[index] }),
    );
  };

  const getTabIndex = () => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    return tabs.indexOf(selectedVoiceTab);
  };

  const filteredAgents = agents;

  if (selectedVoiceTab === "phone-numbers") return <VoicePhoneNumbers />;
  if (selectedVoiceTab === "knowledge-base") return <VoiceKnowledgeBase />;

  // CHANGE: removed full-screen loading gate; render shell immediately

  const disabled = credits !== null && credits < 0.01;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg: selectedVoiceTab === "agents" ? "white" : "gray.200",
                }}
              >
                🤖 Voice Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedVoiceTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📞 Voice Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedVoiceTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{ borderColor: "primary.400", shadow: "md" }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
              isDisabled={disabled}
              opacity={disabled ? 0.6 : 1}
              title={
                disabled
                  ? "Insufficient balance. Please add balance to view agents."
                  : undefined
              }
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>
              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}
              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          <ImportAgent
            onAgentImported={handleAgentCreated}
            buttonProps={{ variant: "outline", isDisabled: disabled }}
          />
          <CreateAgent
            onAgentCreated={handleAgentCreated}
            buttonProps={{ variant: "brandGradient", isDisabled: disabled }}
          />
        </HStack>
      </Flex>

      {filteredAgents.length > 0 && (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
          gap={4}
          mb={6}
        >
          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Agents</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? <Spinner size="sm" /> : stats.totalAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Agents</StatLabel>
                <StatNumber color="green.500">
                  {loading.metrics ? <Spinner size="sm" /> : stats.activeAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Interactions</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? (
                    <Spinner size="sm" />
                  ) : (
                    stats.totalInteractions
                  )}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Success Rate</StatLabel>
                <StatNumber color="blue.500">
                  {loading.metrics ? (
                    <Spinner size="sm" />
                  ) : (
                    `${stats.avgSuccessRate}%`
                  )}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {disabled && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Account Suspended</AlertTitle>
            <AlertDescription>Your account has no credits. Agents are paused. <Link href="/billing" color="red.700" fontWeight="bold">Add credits →</Link></AlertDescription>
          </Box>
        </Alert>
      )}

      {filteredAgents.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          {loading.agents ? (
            <Spinner size="xl" color="primary.500" />
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} color="gray" size="3x" />
              <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
                No Voice Agents Found!
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Create a voice agent to get started.
              </Text>
              <Box mt={6}>
                <CreateAgent
                  onAgentCreated={handleAgentCreated}
                  buttonProps={{
                    colorScheme: "primary",
                    bg: "primary.800",
                    _hover: { bg: "primary.900" },
                    isDisabled: disabled,
                  }}
                />
              </Box>
            </>
          )}
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(520px, 1fr))" gap={6}>
          {filteredAgents.map((agent) => {
            const voice = voiceDetails[agent.voice_id];
            const agentPhones = phoneNumbers[agent.agent_id] || [];
            const primaryPhone = agentPhones[0];
            const hasPhone = agentPhones.length > 0;

            const { agentTypeLabel, agentTypeColor } = getVoiceAgentType(
              agent,
              llmData,
            );

            const cardDisabled = disabled;
            const isPausedByUser = agent.is_active === false;

            return (
              <Card
                role="button"
                tabIndex={cardDisabled ? -1 : 0}
                aria-disabled={cardDisabled}
                key={agent.agent_id}
                borderLeft="4px"
                borderLeftColor="primary.500"
                cursor={cardDisabled ? "not-allowed" : "pointer"}
                opacity={cardDisabled ? 0.55 : 1}
                onClick={() => {
                  if (cardDisabled) return;
                  navigate(`/voice-agents/${agent.agent_id}`);
                }}
                _hover={cardDisabled ? {} : { shadow: "md" }}
                transition="all 0.2s"
                minH="250px"
                display="flex"
                flexDirection="column"
                title={
                  cardDisabled
                    ? "Insufficient balance to view agent details"
                    : undefined
                }
              >
                <CardBody display="flex" flexDirection="column" p={5}>
                  <VStack align="stretch" spacing={3} flex={1}>
                    {/* Header */}
                    <Flex justify="space-between" align="start" gap={3}>
                      <HStack align="start" spacing={3} flex={1} minW={0}>
                        {/* Avatar */}
                        <Box
                          w="52px"
                          h="52px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor="primary.500"
                          overflow="hidden"
                          flexShrink={0}
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {voice ? (
                            <GenderImage voice={voice} boxSize="32px" mr={0} />
                          ) : loading.voiceDetails ? (
                            <Spinner size="sm" />
                          ) : (
                            <Avatar size="sm" bg="gray.200" />
                          )}
                        </Box>

                        <VStack align="start" spacing={1} flex={1} minW={0}>
                          <Text
                            fontSize="lg"
                            fontWeight="600"
                            color="black"
                            noOfLines={2}
                            lineHeight="1.2"
                          >
                            {agent.agent_name || "Unnamed Agent"}
                          </Text>

                          <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme="purple" fontSize="xs">
                              VOICE
                            </Badge>

                            <Badge
                              colorScheme={isPausedByUser ? 'red' : (hasPhone ? 'green' : 'yellow')}
                              fontSize="xs"
                            >
                              {isPausedByUser ? 'PAUSED' : (hasPhone ? 'ACTIVE' : 'NO PHONE')}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>

                      {/* Actions */}
                      <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          aria-label={agent.is_active === false ? "Resume agent" : "Pause agent"}
                          icon={<FontAwesomeIcon icon={agent.is_active === false ? faPlay : faPause} />}
                          size="sm"
                          variant="ghost"
                          colorScheme={agent.is_active === false ? "green" : "orange"}
                          onClick={() => handlePauseResumeAgent(agent.agent_id, agent.is_active !== false)}
                          title={agent.is_active === false ? "Resume agent" : "Pause agent"}
                          isLoading={pausingAgentId === agent.agent_id}
                        />
                        <IconButton
                          aria-label="Edit agent"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigate(`/voice-agents/${agent.agent_id}`)
                          }
                          isDisabled={cardDisabled}
                          title={
                            cardDisabled
                              ? "Insufficient balance to edit agent"
                              : "Edit"
                          }
                        />
                        <IconButton
                          aria-label="Delete agent"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => setDeleteAgentId(agent.agent_id)}
                          title="Delete"
                        />
                      </HStack>
                    </Flex>

                    {/* Details (similar layout to phone numbers card) */}
                    <VStack
                      align="stretch"
                      spacing={2}
                      pt={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Phone Number:
                        </Text>

                        {loading.phoneNumbers ? (
                          <Spinner size="xs" />
                        ) : hasPhone ? (
                          <Badge
                            colorScheme="green"
                            fontSize="xs"
                            display="inline-flex"
                            alignItems="center"
                            gap={2}
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            <FontAwesomeIcon icon={faPhone} />
                            <Text as="span">{primaryPhone}</Text>
                          </Badge>
                        ) : (
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            Not connected
                          </Text>
                        )}
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Voice:
                        </Text>

                        {loading.voiceDetails ? (
                          <Spinner size="xs" />
                        ) : (
                          <Badge
                            bg="pink.200"
                            color="gray.800"
                            fontSize="xs"
                            fontWeight="600"
                            px={2}
                            py={1}
                            borderRadius="md"
                            maxW="260px"
                            noOfLines={1}
                          >
                            {voice?.voice_name || "—"}
                          </Badge>
                        )}
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Agent Type:
                        </Text>

                        {loading.llmData ? (
                          <Spinner size="xs" />
                        ) : (
                          <Badge colorScheme={agentTypeColor} fontSize="xs">
                            {agentTypeLabel}
                          </Badge>
                        )}
                      </Flex>
                    </VStack>

                    {/* Footer (Last updated bottom-right) */}
                    <Flex
                      pt={2}
                      mt="auto"
                      borderTop="1px solid"
                      borderColor="gray.100"
                      justify="flex-end"
                    >
                      <Text fontSize="xs" color="gray.500">
                        Last Updated:{" "}
                        {agent.last_modification_timestamp ? new Date(
                          agent.last_modification_timestamp,
                        ).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        }) : '—'}
                      </Text>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      <AlertDialog isOpen={!!deleteAgentId} leastDestructiveRef={cancelRef} onClose={() => setDeleteAgentId(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete Agent</AlertDialogHeader>
            <AlertDialogBody>This will permanently delete the agent. This cannot be undone.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteAgentId(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={() => { handleDeleteAgent(deleteAgentId!); setDeleteAgentId(null); }} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VoiceAgents;
